import { useRef, useState, useEffect, useCallback } from "react";

/**
 * Hüllt eine horizontal scrollbare Leiste (z.B. TabsList) ein und zeigt
 * dezente Fade-/Schatten-Indikatoren links/rechts an, solange in die jeweilige
 * Richtung weitergescrollt werden kann. So erkennen Nutzer auf dem Handy sofort,
 * dass weitere Einträge durch Wischen erreichbar sind.
 *
 * Zusätzlich wird der aktive Tab (`[data-state="active"]`) bei jedem Wechsel
 * automatisch horizontal in den sichtbaren Bereich zentriert.
 */
export const ScrollFade = ({ children, className = "" }) => {
  const ref = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setShowLeft(scrollLeft > 4);
    setShowRight(scrollLeft + clientWidth < scrollWidth - 4);
  }, []);

  const centerActive = useCallback((smooth = true) => {
    const el = ref.current;
    if (!el) return;
    const active = el.querySelector('[data-state="active"]');
    if (!active) return;
    const containerRect = el.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    const target =
      (activeRect.left - containerRect.left) +
      el.scrollLeft -
      el.clientWidth / 2 +
      activeRect.width / 2;
    el.scrollTo({ left: Math.max(0, target), behavior: smooth ? "smooth" : "auto" });
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    update();
    centerActive(false);

    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);

    // Beobachtet data-state-Wechsel der Tabs und zentriert den aktiven Tab.
    const observer = new MutationObserver((mutations) => {
      if (mutations.some((m) => m.attributeName === "data-state")) {
        centerActive(true);
        window.requestAnimationFrame(update);
      }
    });
    observer.observe(el, {
      attributes: true,
      subtree: true,
      attributeFilter: ["data-state"],
    });

    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      observer.disconnect();
    };
  }, [update, centerActive]);

  return (
    <div className={`relative ${className}`} data-testid="scroll-fade">
      <div
        ref={ref}
        className="overflow-x-auto no-scrollbar"
        onScroll={update}
      >
        {children}
      </div>
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-background to-transparent transition-opacity duration-200 ${showLeft ? "opacity-100" : "opacity-0"}`}
      />
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-background to-transparent transition-opacity duration-200 ${showRight ? "opacity-100" : "opacity-0"}`}
      />
    </div>
  );
};
